import { CommonModule } from '@angular/common';
import {
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { LayoutModule } from '../../../layout/layout.module';
import {
  DashboardNotification,
  DashboardNotificationKind,
  NotificationService,
} from '../../../services/NotificationService';

type NotificationFilter = 'all' | 'read' | 'unread';

@Component({
  selector: 'app-notifications-dashboard-page',
  standalone: true,
  imports: [
    CommonModule,
    LayoutModule,
  ],
  templateUrl: './notifications-dashboard-page.component.html',
  styleUrl: './notifications-dashboard-page.component.sass',
})
export class NotificationsDashboardPageComponent implements OnInit, OnDestroy {
  public notifications: DashboardNotification[] = [];
  public activeFilter: NotificationFilter = 'all';
  private subscription: Subscription | null = null;

  public constructor(
    private notificationService: NotificationService,
  ) { }

  public ngOnInit(): void {
    this.subscription = this.notificationService.notifications$.subscribe((notifications) => {
      this.notifications = notifications;
    });
    void this.notificationService.refresh();
  }

  public ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  public get unreadCount(): number {
    return this.notifications.filter((notification) => !notification.read).length;
  }

  public get readCount(): number {
    return this.notifications.filter((notification) => notification.read).length;
  }

  public get filteredNotifications(): DashboardNotification[] {
    if (this.activeFilter === 'unread') {
      return this.notifications.filter((notification) => !notification.read);
    }

    if (this.activeFilter === 'read') {
      return this.notifications.filter((notification) => notification.read);
    }

    return this.notifications;
  }

  public setFilter(filter: NotificationFilter): void {
    this.activeFilter = filter;
  }

  public markAllAsRead(): void {
    this.notificationService.markAllAsRead();
  }

  public markAsRead(notification: DashboardNotification): void {
    if (!notification.read) {
      this.notificationService.markAsRead(notification.id);
    }
  }

  public getKindLabel(kind: DashboardNotificationKind): string {
    const labels: Record<DashboardNotificationKind, string> = {
      calendar: 'Календарь',
      client: 'Клиенты',
      directory: 'Справочник',
      event: 'События',
      finance: 'Финансы',
      kanban: 'Канбан',
      mailing: 'Рассылки',
      order: 'Заказы',
      system: 'Система',
      telegram: 'Telegram',
      user: 'Пользователи',
    };

    return labels[kind];
  }

  public formatRelativeTime(value: string): string {
    const seconds = Math.max(Math.floor((Date.now() - new Date(value).getTime()) / 1000), 0);
    if (seconds < 60) {
      return 'сейчас';
    }

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
      return `${minutes.toString()} мин назад`;
    }

    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return `${hours.toString()} ч назад`;
    }

    const days = Math.floor(hours / 24);
    if (days < 7) {
      return `${days.toString()} дн назад`;
    }

    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value));
  }

  public trackByNotificationId(_index: number, notification: DashboardNotification): string {
    return notification.id;
  }
}
